<template>
  <div>
    <!-- TITLE AND PRICING BLOCK -->
    <div class="mb-4">
      <plan-header :subscription="subscription"></plan-header>
    </div>

    <div v-if="subscription.plan !== 'default'">
      <!-- READ ONLY BLOCK : SUMMARY OR THE SUBSCRIPTION -->
      <div v-if="readonly" class="mb-4">
        <slot name="read-only-prepend"></slot>
        <slot name="read-only-billing-cycle">
          <div :class="['active', 'trialing'].includes(subscription.status) ? '' : 'warning--text font-weight-bold'">
            <strong>{{ $t('subscription-status') }}</strong> :
            <span v-if="['active', 'trialing'].includes(subscription.status)">{{ $t('active') }}</span>
            <span v-else>{{ $t('inactive') }}</span>
          </div>
        </slot>
        <slot name="read-only-billing-cycle">
          <div>
            <strong>{{ $t('billing-cycle') }}</strong> :
            <span v-if="subscription.yearly">{{ $t('yearly') }}</span>
            <span v-else>{{ $t('monthly') }}</span>
          </div>
        </slot>
        <slot name="read-only-subscription-period-start">
          <div>
            <strong>{{ $t('subscription-period') }}</strong> :
            {{ subscription.period.start ? $helpers.present.date(subscription.period.start) : $t('none') }}
          </div>
        </slot>
        <slot name="read-only-subscription-period-end">
          <div v-if="subscription.ends_at === null">
            <span v-if="subscription.update_at_end !== null" class="warning--text">
              <strong>{{ $t('subscription-period-end') }}</strong> :
              {{ subscription.period.end ? $helpers.present.date(subscription.period.end) : $t('none') }}
              <v-btn text small color="warning"
                     @click="openChangePlanCancelDialog">{{ $t('remove-modifications') }}</v-btn>
            </span>
            <span v-else>
              <strong>{{ $t('subscription-period-date') }}</strong> :
              {{ subscription.period.end ? $helpers.present.date(subscription.period.end) : $t('none') }}
            </span>
          </div>
          <div v-else class="error--text">
            <strong>{{ $t('cancellation-date') }}</strong> :
            {{ subscription.period.end ? $helpers.present.date(subscription.period.end) : $t('none') }}
          </div>
        </slot>
        <slot name="read-only-reduction">
          <div v-if="subscription.coupon.id !== null">
            <strong>{{ $t('discount-coupon') }}</strong> : -
            <span v-if="subscription.coupon.percentage">{{ subscription.coupon.amount }} %</span>
            <span v-else>{{ $helpers.present.currency(subscription.coupon.amount) }}</span>
          </div>
        </slot>
        <slot name="read-only-options">
          <div>
            <strong>{{ $t('options') }}</strong>
            <ul>
              <li v-for="(quantity, option) in subscription.options"
                  :key="subscription.module + subscription.plan + option">
                {{ $t('subscriptions.' + subscription.module + '.options.' + option + '.name') }} x {{ subscription.options[option] }}
              </li>
            </ul>
          </div>
        </slot>
        <slot name="read-only-append"></slot>
      </div>

      <!-- EDITABLE BLOCK : DESCRIPTION, YEARLY SELECTOR, OPTIONS SELECTOR -->
      <div v-else>
        <slot name="editable-yearly-switch">
          <div v-if="subscription.pricing.yearly && subscription.pricing.monthly">
            <v-switch
                v-model="subscription.yearly"
                :label="$t('yearly-payment')">
            </v-switch>
          </div>
        </slot>
        <slot name="editable-options">
          <div v-if="Object.keys(subscription.options).length > 0" class="mb-4">
            <strong>{{ $t('options') }}</strong>
            <div
                v-for="(quantity, option) in subscription.options"
                :key="subscription.module + subscription.plan + option"
                class="ml-4">
            <span v-if="subscription.moduleConfig.plan.options[option].multiple">
              <v-chip
                  outlined
                  :disabled="subscription.options[option] === 0"
                  small
                  color="primary">
                x {{ subscription.options[option] }}</v-chip>
              {{ $t('subscriptions.' + subscription.module + '.options.' + option + '.name') }}
              <v-btn icon :disabled="subscription.options[option] < 1" small color="primary"
                     @click.stop="subscription.options[option] -= 1">
                <v-icon>mdi-minus</v-icon>
              </v-btn>
              <v-btn icon small color="primary" @click.stop="subscription.options[option] += 1">
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </span>
            </div>
          </div>
        </slot>
      </div>
    </div>

    <div class="text--primary mb-4">
      <slot name="arguments"></slot>
      <plan-arguments :subscription="subscription"></plan-arguments>
    </div>
  </div>
</template>

<script>
import PlanHeader from './Common/PlanHeader'
import PlanArguments from './Common/PlanArguments'

export default {
  name: 'SubscriptionPlanContent',
  components: { PlanArguments, PlanHeader },
  props: ['subscription', 'readonly'],
  methods: {
    openChangePlanCancelDialog() {
      this.$ui.openDialog('change_plan_cancel', this.subscription)
    }
  }
}
</script>

<style scoped>

</style>

<i18n>
{
	"en": {
		"active": "active",
		"billing-cycle": "Billing cycle",
		"cancellation-date": "Cancellation registered for",
		"discount-coupon": "Discount coupon",
		"inactive": "inactive",
		"monthly": "monthly",
		"none": "Any",
		"options": "Options",
		"remove-modifications": "View / Delete changes",
		"subscription-period": "Subscription on",
		"subscription-period-date": "Renewal on",
		"subscription-period-end": "Renewal with modification on",
		"subscription-status": "Subscription",
		"yearly": "annual",
		"yearly-payment": "Yearly payment"
	},
	"es": {
		"active": "activo",
		"billing-cycle": "Ciclo de facturación",
		"cancellation-date": "Rescisión registrada para",
		"discount-coupon": "Cupón de descuento",
		"inactive": "inactivo",
		"monthly": "mensual",
		"none": "No",
		"options": "Opciones",
		"remove-modifications": "Ver / Eliminar cambios",
		"subscription-period": "Suscripción en",
		"subscription-period-date": "Renovación en",
		"subscription-period-end": "Renovación con modificación en",
		"subscription-status": "Suscripción",
		"yearly": "anual",
		"yearly-payment": "Pago anual"
	},
	"fr": {
		"active": "active",
		"billing-cycle": "Cycle de facturation",
		"cancellation-date": "Résiliation enregistrée pour le",
		"discount-coupon": "Coupon de réduction",
		"inactive": "inactive",
		"monthly": "mensuel",
		"none": "Aucune",
		"options": "Options",
		"remove-modifications": "Voir / Supprimer les modifications",
		"subscription-period": "Souscription le",
		"subscription-period-date": "Renouvellement le",
		"subscription-period-end": "Renouvellement avec modification le",
		"subscription-status": "Souscription",
		"yearly": "annuel",
		"yearly-payment": "Paiement à l'année"
	},
	"it": {
		"active": "attivo",
		"billing-cycle": "Ciclo di fatturazione",
		"cancellation-date": "Disdetta registrata per il",
		"discount-coupon": "Coupon sconto",
		"inactive": "inattivo",
		"monthly": "mensile",
		"none": "Nessuno",
		"options": "Opzioni",
		"remove-modifications": "Visualizza / Elimina modifiche",
		"subscription-period": "Abbonamento il",
		"subscription-period-date": "Rinnovo il",
		"subscription-period-end": "Rinnovo con modifica il",
		"subscription-status": "Abbonamento",
		"yearly": "annuale",
		"yearly-payment": "Pagamento annuale"
	},
	"pt": {
		"active": "ativa",
		"billing-cycle": "Ciclo de faturação",
		"cancellation-date": "Cancelamento registado para",
		"discount-coupon": "Cupão de desconto",
		"inactive": "inativa",
		"monthly": "mensal",
		"none": "Nenhum",
		"options": "Opções",
		"remove-modifications": "Ver / Eliminar alterações",
		"subscription-period": "Subscrição em",
		"subscription-period-date": "Renovação em",
		"subscription-period-end": "Renovação com alteração em",
		"subscription-status": "Subscrição",
		"yearly": "anual",
		"yearly-payment": "Pagamento anual"
	},
	"pl": {
		"active": "aktywna",
		"billing-cycle": "Cykl rozliczeniowy",
		"cancellation-date": "Wypowiedzenie zarejestrowane na",
		"discount-coupon": "Kupon rabatowy",
		"inactive": "nieaktywna",
		"monthly": "miesięczny",
		"none": "Żadna",
		"options": "Opcje",
		"remove-modifications": "Zobacz / Usuń zmiany",
		"subscription-period": "Subskrypcja dnia",
		"subscription-period-date": "Odnowienie dnia",
		"subscription-period-end": "Odnowienie ze zmianą dnia",
		"subscription-status": "Subskrypcja",
		"yearly": "roczny",
		"yearly-payment": "Płatność roczna"
	}
}
</i18n>
